import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import { mintOpId } from '../../components/Agents/opId';
import {
  type Feed,
  type FeedCard,
  feedErrorMessage,
  getFeed as apiGetFeed,
  confirmPost as apiConfirm,
  likePost as apiLike,
  contestPost as apiContest,
  approvePost as apiApprove,
  commentPost as apiComment,
} from '../../components/Feed/api';

export interface FeedSlice {
  /** The feed as last fetched — posts already disclosed for the viewer's policy. */
  feed: Feed | null;
  feedLoading: boolean;
  feedError: string | null;
  /** Bumped on every identity/org switch; a result minted before it is dropped. */
  feedGeneration: number;

  fetchFeed: () => Promise<void>;
  /** Drop the previous workspace's feed on an account/org switch and reload if on-screen. */
  resetFeedWorkspace: () => void;
  /** Drop the cached feed and reload if on-screen — the disclosure changed (e.g. the viewer's evidence policy). */
  invalidateFeed: () => void;
  /** Confirm a post: reflects `you.confirmed` and bumps its confirm count in place. */
  confirmPost: (id: string) => Promise<void>;
  /** Like a post: reflects `you.liked` and bumps its like count in place. */
  likePost: (id: string) => Promise<void>;
  /** Contest a post with a required reason; reflects `you.contested`, the count, and the reason. */
  contestPost: (id: string, reason: string) => Promise<void>;
  /** Sign off on a proposal (#201): the card shows "approved" and the approver. Executes nothing. */
  approvePost: (id: string) => Promise<void>;
  /** Comment = steer the agent (#202); a fresh op_id per logical comment. Nothing to render back. */
  commentPost: (id: string, text: string, opId?: string) => Promise<void>;
}

/** Replace one card in the feed, leaving the rest and the policy block untouched. */
const patchCard = (feed: Feed | null, id: string, patch: (c: FeedCard) => FeedCard): Feed | null => {
  if (!feed) return feed;
  return { ...feed, posts: feed.posts.map((p) => (p.id === id ? patch(p) : p)) };
};

/** Set an agent's earned reach on every card that agent authored — reach is a per-agent
 *  aggregate shown on all of its cards, so approve/contest set the server's new value on all. */
const setAgentReach = (feed: Feed | null, agentId: string, reach: number | null): Feed | null => {
  if (!feed || typeof reach !== 'number') return feed;
  return { ...feed, posts: feed.posts.map((p) => (p.agent_id === agentId ? { ...p, reach } : p)) };
};

export const createFeedSlice: StateCreator<VoyagerState, [], [], FeedSlice> = (set, get) => {
  // A monotonic feed epoch: each fetch AND each applied mutation bumps it, so a
  // fetch writes its snapshot only when nothing newer happened while it was in
  // flight — neither a later fetch nor an optimistic mutation gets clobbered by
  // an older refresh landing late. (feedGeneration is the coarser reset for a
  // workspace/policy change; epoch is the fine-grained request ordering.)
  let epoch = 0;
  const touch = () => { epoch += 1; return epoch; };
  const stale = (gen: number, mine: number) => get().feedGeneration !== gen || mine !== epoch;
  // A fetch owns the loading flag only while it is the LATEST fetch. Snapshot
  // validity (epoch) and loading ownership (fetchSeq) are tracked separately so
  // a mutation that supersedes an in-flight refresh's snapshot doesn't strand
  // feedLoading=true — the refresh is still the latest fetch, so it clears it.
  let fetchSeq = 0;
  // Reach is a per-agent aggregate; two approvals/contests on the same agent can
  // have their responses arrive out of order. A monotonic per-agent reach seq
  // lets a later-issued write win: an older response never overwrites the reach
  // a newer one already applied (the card's own approve/contest state still lands).
  let reachSeq = 0;
  const reachApplied = new Map<string, number>();
  const takeReach = () => (++reachSeq);
  const reachIsLatest = (agentId: string, mine: number) => {
    if ((reachApplied.get(agentId) ?? 0) >= mine) return false;
    reachApplied.set(agentId, mine);
    return true;
  };

  return {
    feed: null,
    feedLoading: false,
    feedError: null,
    feedGeneration: 0,

    resetFeedWorkspace: () => get().invalidateFeed(),

    invalidateFeed: () => {
      // a feed disclosed under the previous workspace or a stale evidence policy
      // must neither linger on screen nor arrive late from an in-flight request;
      // bumping the generation drops any fetch/mutation still awaiting
      set({ feed: null, feedLoading: false, feedError: null, feedGeneration: get().feedGeneration + 1 });
      if (get().currentView === 'feed') get().fetchFeed().catch(() => {});
    },

    fetchFeed: async () => {
      const gen = get().feedGeneration;
      const mine = touch();
      const myFetch = ++fetchSeq;
      // the latest fetch clears loading, whatever became of its snapshot
      const done = () => { if (myFetch === fetchSeq) set({ feedLoading: false }); };
      set({ feedLoading: true, feedError: null });
      try {
        const feed = await apiGetFeed();
        if (!stale(gen, mine)) set({ feed });   // a switch, a newer fetch, or a mutation may have landed
        done();
      } catch (e: any) {
        if (!stale(gen, mine)) set({ feedError: feedErrorMessage(e, 'Could not load the feed') });
        done();
      }
    },

    confirmPost: async (id) => {
      const gen = get().feedGeneration;
      await apiConfirm(id);
      if (get().feedGeneration !== gen) return;
      touch();   // supersede any refresh still in flight — its snapshot predates this
      set((s) => ({ feed: patchCard(s.feed, id, (c) => (c.you.confirmed ? c : {
        ...c, you: { ...c.you, confirmed: true }, stats: { ...c.stats, confirms: c.stats.confirms + 1 },
      })) }));
    },

    likePost: async (id) => {
      const gen = get().feedGeneration;
      await apiLike(id);
      if (get().feedGeneration !== gen) return;
      touch();
      set((s) => ({ feed: patchCard(s.feed, id, (c) => (c.you.liked ? c : {
        ...c, you: { ...c.you, liked: true }, stats: { ...c.stats, likes: c.stats.likes + 1 },
      })) }));
    },

    contestPost: async (id, reason) => {
      const trimmed = reason.trim();
      if (!trimmed) throw new Error('a contest needs a reason');
      const gen = get().feedGeneration;
      const agentId = get().feed?.posts.find((p) => p.id === id)?.agent_id;
      const myReach = agentId ? takeReach() : 0;
      const res = await apiContest(id, trimmed);
      if (get().feedGeneration !== gen) return;
      touch();
      // attribute the contest to its own signed provenance, not the seat holder,
      // and show the reason the SERVER recorded — a retry after a lost response
      // returns the first (deduped) reaction, whose reason may differ from this edit
      const rx = res.reaction || {};
      const asserted = rx.provenance?.asserted_by_name;
      const by = (typeof asserted === 'string' && asserted) || 'You';
      const persisted = (typeof rx.reason === 'string' && rx.reason) || trimmed;
      set((s) => {
        const c = s.feed?.posts.find((p) => p.id === id);
        if (!c || c.you.contested) return {};   // idempotent: this viewer already contested
        const patched = patchCard(s.feed, id, (cc) => ({
          ...cc,
          you: { ...cc.you, contested: true },
          stats: { ...cc.stats, contests: cc.stats.contests + 1 },
          contest_reasons: [...cc.contest_reasons, { by, reason: persisted }],
        }));
        // set the SERVER's authoritative reach (a client delta can't recover a
        // floored total), unless a later-issued write for this agent already did
        const ag = res.agent_id || c.agent_id;
        return { feed: reachIsLatest(ag, myReach) ? setAgentReach(patched, ag, res.reach) : patched };
      });
    },

    approvePost: async (id) => {
      const gen = get().feedGeneration;
      const agentId = get().feed?.posts.find((p) => p.id === id)?.agent_id;
      const myReach = agentId ? takeReach() : 0;
      const res = await apiApprove(id);
      if (get().feedGeneration !== gen) return;
      touch();
      set((s) => {
        const c = s.feed?.posts.find((p) => p.id === id);
        if (!c) return {};
        const patched = patchCard(s.feed, id, (cc) => ({
          ...cc,
          approved: res.approved ?? cc.approved,
          // approve also records a confirm server-side (#201) — reflect that count
          // once, only if this viewer had not already confirmed
          stats: cc.you.confirmed ? cc.stats : { ...cc.stats, confirms: cc.stats.confirms + 1 },
          you: { ...cc.you, approved: true, confirmed: true },
        }));
        // set the SERVER's authoritative reach on every card of this agent,
        // unless a later-issued write for this agent already applied a newer one
        const ag = res.agent_id || c.agent_id;
        return { feed: reachIsLatest(ag, myReach) ? setAgentReach(patched, ag, res.reach) : patched };
      });
    },

    commentPost: async (id, text, opId) => {
      await apiComment(id, text, opId || mintOpId());
    },
  };
};
